import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import MoleculeViewer from "../components/ui/3dmols";
import Navbar from "../components/ui/navbar";
import { ILnp } from "../type";
import { Backend } from "../url";
interface TableOfContentsItem {
  id: string;
  title: string;
}

const detailPageTableOfContents: TableOfContentsItem[] = [
  { id: "LNP ID", title: "LNP ID" },
  { id: "lipid-components", title: "Composition" },
  { id: "experimental-conditions", title: "Experimental Conditions" },
  { id: "molecular-properties", title: "Molecular Properties" },
  { id: "publication", title: "Publication" },
];

interface TableOfContentsProps {
  tableOfContentsItems: TableOfContentsItem[];
  activeId: string;
  setActiveId: (id: string) => void;
  navbarHeight?: string;
}

const TableOfContents: React.FC<TableOfContentsProps> = ({
  tableOfContentsItems,
  activeId,
  setActiveId,
  navbarHeight = "4rem",
}) => {
  const handleClick = (id: string) => {
    setActiveId(id);

    const target = document.getElementById(id);
    if (target) {
      const navbarElement = document.querySelector("nav.fixed.top-0.z-50");
      const navHeight = navbarElement
        ? navbarElement.getBoundingClientRect().height
        : parseFloat(navbarHeight) * 16;

      const targetPosition =
        target.getBoundingClientRect().top +
        window.pageYOffset -
        navHeight -
        10;

      window.scrollTo({
        top: targetPosition,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="fixed left-0 w-64" style={{ top: navbarHeight }}>
      <ul
        className="flex-shrink-0 w-full px-6 py-4 overflow-y-auto bg-gray-100 border-gray-300 rounded"
        style={{ maxHeight: `calc(100vh - ${navbarHeight} - 2rem)` }}
      >
        {tableOfContentsItems.map((item) => (
          <li
            key={item.id}
            className={`py-2 px-4 rounded-md transition-colors duration-200 ${
              activeId === item.id
                ? "bg-blue-500 text-white font-bold"
                : "hover:bg-blue-100"
            }`}
          >
            <a
              href={`#${item.id}`}
              onClick={(e) => {
                e.preventDefault();
                handleClick(item.id);
              }}
              className="block w-full"
            >
              {item.title}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
};

const DetailPage: React.FC = () => {
  const { pk } = useParams<{ pk: string }>();
  const contentRef = useRef<HTMLDivElement>(null);
  const [activeId, setActiveId] = useState<string>(
    detailPageTableOfContents[0].id,
  );
  const [data, setData] = useState<ILnp | null>(null);
  const [navbarHeight, setNavbarHeight] = useState(0);
  const [show3D, setShow3D] = useState(true);

  useEffect(() => {
    const navElement = document.querySelector("nav.fixed.top-0.z-50");
    if (navElement) {
      setNavbarHeight(navElement.getBoundingClientRect().height);
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current || navbarHeight === 0) return;

      let currentActiveId = "";
      const scrollThreshold = navbarHeight + 20;

      for (const item of detailPageTableOfContents) {
        const element = document.getElementById(item.id);
        if (element) {
          const elementRect = element.getBoundingClientRect();

          if (elementRect.top <= scrollThreshold) {
            currentActiveId = item.id;
          } else {
            if (!currentActiveId) {
              currentActiveId = detailPageTableOfContents[0].id;
            }
          }
        }
      }

      if (window.pageYOffset < 50 && detailPageTableOfContents.length > 0) {
        currentActiveId = detailPageTableOfContents[0].id;
      }

      if (currentActiveId && currentActiveId !== activeId) {
        setActiveId(currentActiveId);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [activeId, navbarHeight]);

  useEffect(() => {
    const getDetails = async () => {
      if (!pk) return;
      try {
        const response = await fetch(`${Backend}/search/textsearch/${pk}`);
        if (!response.ok) {
          throw new Error(
            `Network response was not ok: ${response.statusText}`,
          );
        }
        const result = await response.json();
        setData(result);
      } catch (error) {
        console.error("Error fetching LNP details:", error);
        setData(null);
      }
    };
    getDetails();
  }, [pk]);

  if (!data) {
    return (
      <>
        <Navbar />
        <div className="flex items-center justify-center h-screen">
          Loading...
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="flex pt-4">
        <TableOfContents
          tableOfContentsItems={detailPageTableOfContents}
          activeId={activeId}
          setActiveId={setActiveId}
          navbarHeight={`${navbarHeight}px`}
        />

        <div
          className="w-full p-8 ml-64"
          ref={contentRef}
          style={{ paddingTop: `${navbarHeight}px` }}
        >
          <section id="LNP ID">
            <h2 className="my-6 text-4xl font-bold">
              LNP-{String(data.index).padStart(7, "0")}
            </h2>
            <div className="grid grid-cols-1 gap-6 p-2 md:grid-cols-2 md:gap-8">
              <div className="flex flex-col">
                <h3 className="mb-3 text-2xl font-semibold text-gray-700">
                  LNP Summary
                </h3>
                <div className="relative overflow-x-auto border rounded-lg shadow-md">
                  <table className="w-full text-sm text-left">
                    <tbody>
                      <tr className="bg-white border-b">
                        <th className="w-1/3 px-4 py-3 font-medium text-gray-800 bg-gray-50 whitespace-nowrap">
                          LNP ID
                        </th>
                        <td className="p-3">
                          LNP-{String(data.index).padStart(7, "0")}
                        </td>
                      </tr>
                      <tr className="bg-white border-b">
                        <th className="w-1/3 px-4 py-3 font-medium text-gray-800 bg-gray-50 whitespace-nowrap">
                          Experiment Code
                        </th>
                        <td className="p-3">
                          <Link
                            to={`/search/text?filter=${encodeURIComponent(
                              JSON.stringify({
                                experiment_id: data.experiment_id,
                              }),
                            )}`}
                            className="font-medium text-blue-600 hover:underline"
                          >
                            {data.experiment_id}
                          </Link>
                        </td>
                      </tr>

                      <tr className="bg-white border-b">
                        <th
                          scope="row"
                          className="w-1/3 px-4 py-3 font-medium text-gray-800 bg-gray-50 whitespace-nowrap"
                        >
                          Ionizable Lipid (IL)
                        </th>
                        <td className="px-4 py-3 text-gray-700">
                          {data.il_name || "N/A"} (
                          {data.il_molratio != null
                            ? `${data.il_molratio} %`
                            : "N/A"}
                          )
                        </td>
                      </tr>
                      <tr className="bg-white border-b">
                        <th
                          scope="row"
                          className="w-1/3 px-4 py-3 font-medium text-gray-800 bg-gray-50 whitespace-nowrap"
                        >
                          Helper Lipid (HL)
                        </th>
                        <td className="px-4 py-3 text-gray-700">
                          {data.hl_name || "N/A"} (
                          {data.hl_molratio != null
                            ? `${data.hl_molratio} %`
                            : "N/A"}
                          )
                        </td>
                      </tr>
                      <tr className="bg-white border-b">
                        <th
                          scope="row"
                          className="w-1/3 px-4 py-3 font-medium text-gray-800 bg-gray-50 whitespace-nowrap"
                        >
                          PEG Lipid (PEG)
                        </th>
                        <td className="px-4 py-3 text-gray-700">
                          {data.peg_name || "N/A"} (
                          {data.peg_molratio != null
                            ? `${data.peg_molratio} %`
                            : "N/A"}
                          )
                        </td>
                      </tr>
                      <tr className="bg-white border-b">
                        <th
                          scope="row"
                          className="w-1/3 px-4 py-3 font-medium text-gray-800 bg-gray-50 whitespace-nowrap"
                        >
                          Cholesterol (CHL)
                        </th>
                        <td className="px-4 py-3 text-gray-700">
                          {data.chl_name || "N/A"} (
                          {data.chl_molratio != null
                            ? `${data.chl_molratio} %`
                            : "N/A"}
                          )
                        </td>
                      </tr>
                      <tr className="bg-white">
                        <th
                          scope="row"
                          className="w-1/3 px-4 py-3 font-medium text-gray-800 bg-gray-50 whitespace-nowrap"
                        >
                          CHARMM FF Downloads
                        </th>
                        <td className="px-4 py-3 text-gray-700">
                          <div className="flex flex-wrap justify-start gap-4">
                            <a
                              href={`/prm_files/LNP_${String(
                                data.index,
                              ).padStart(7, "0")}.prm`}
                              download
                              className="flex items-center px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-4 h-4 mr-2"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                />
                              </svg>
                              .prm
                            </a>
                            <a
                              href={`/rtf_files/LNP_${String(
                                data.index,
                              ).padStart(7, "0")}.rtf`}
                              download
                              className="flex items-center px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-4 h-4 mr-2"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                />
                              </svg>
                              .rtf
                            </a>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex flex-col">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-2xl font-semibold text-gray-700">
                    Ionizable Lipid Structure
                  </h3>

                  <div className="flex p-1 space-x-1 bg-gray-200 rounded-lg">
                    <button
                      onClick={() => setShow3D(false)}
                      className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                        !show3D
                          ? "bg-white text-gray-800 shadow"
                          : "text-gray-600 hover:bg-gray-300"
                      }`}
                    >
                      2D Structure
                    </button>
                    <button
                      onClick={() => setShow3D(true)}
                      className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                        show3D
                          ? "bg-white text-gray-800 shadow"
                          : "text-gray-600 hover:bg-gray-300"
                      }`}
                    >
                      3D Structure
                    </button>
                  </div>
                </div>

                <div className="relative w-full p-4 border rounded-lg shadow-md bg-gray-50 min-h-[400px]">
                  <div
                    className={`absolute transition-all duration-0 ${
                      show3D ? "inset-0" : "-left-[9999px]"
                    }`}
                  >
                    <MoleculeViewer sdf={String(data.index).padStart(7, "0")} />
                  </div>
                  <div
                    className={
                      !show3D
                        ? "flex items-center justify-center w-full h-full"
                        : "hidden"
                    }
                  >
                    <img
                      src={`/ionizableLipid/LNP_${String(data.index).padStart(
                        7,
                        "0",
                      )}.svg`}
                      alt={`LNP ${data.index} 2D structure`}
                      className="object-contain max-h-[380px] mx-auto"
                    />
                  </div>
                </div>
              </div>
            </div>

            <hr className="my-8 border-gray-300" />
          </section>

          <section id="lipid-components" className="p-2">
            <h2 className="my-6 text-4xl font-bold">Composition</h2>
            <h3 className="pl-3 text-2xl font-semibold text-gray-600">
              Ionizable Lipid
            </h3>
            <div className="relative overflow-x-auto">
              <div className="mt-8 border rounded-lg shadow-md">
                <table className="w-full text-sm">
                  <thead className="text-sm text-gray-700 uppercase bg-gray-100 rounded-t-lg">
                    <tr>
                      <th
                        scope="col"
                        className="px-6 py-3 font-medium text-gray-800 rounded-t-lg whitespace-nowrap"
                      >
                        SMILES
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-white">
                      <td className="w-full px-6 py-4 text-center text-gray-700 break-all">
                        {data.il_smiles || "N/A"}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <table className="w-full text-sm text-left">
                <tbody>
                  <tr className="bg-white border-b">
                    <th
                      scope="row"
                      className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                    >
                      IL ID
                    </th>
                    <td className="w-1/4 px-6 py-4">{data.il_name || "N/A"}</td>
                    <th
                      scope="row"
                      className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                    >
                      Head
                    </th>
                    <td className="w-1/4 px-6 py-4">
                      {data.il_head_name || "N/A"}
                    </td>
                  </tr>
                  <tr className="bg-white border-b">
                    <th
                      scope="row"
                      className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                    >
                      Linker
                    </th>
                    <td className="w-1/4 px-6 py-4">
                      {data.il_linker_name || "N/A"}
                    </td>
                    <th
                      scope="row"
                      className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                    >
                      Tail 1
                    </th>
                    <td className="w-1/4 px-6 py-4">
                      {data.il_tail1_name || "N/A"}
                    </td>
                  </tr>
                  <tr className="bg-white border-b">
                    <th
                      scope="row"
                      className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                    >
                      Tail 2
                    </th>
                    <td className="w-1/4 px-6 py-4">
                      {data.il_tail2_name || "N/A"}
                    </td>
                    <th
                      scope="row"
                      className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                    >
                      Tail 3
                    </th>
                    <td className="w-1/4 px-6 py-4">
                      {data.il_tail3_name || "N/A"}
                    </td>
                  </tr>
                  <tr className="bg-white border-b">
                    <th
                      scope="row"
                      className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                    >
                      Tail 4
                    </th>
                    <td className="w-1/4 px-6 py-4">
                      {data.il_tail4_name || "N/A"}
                    </td>
                    <th
                      scope="row"
                      className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                    >
                      Molar Ratio
                    </th>
                    <td className="w-1/4 px-6 py-4">
                      {data.il_molratio != null
                        ? `${data.il_molratio} %`
                        : "N/A"}
                    </td>
                  </tr>
                  <tr className="bg-white border-b">
                    <th
                      scope="row"
                      className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                    >
                      IL to nucleicacid Mass Ratio
                    </th>
                    <td className="w-1/4 px-6 py-4">
                      {data.il_to_nucleicacid_massratio != null
                        ? `${data.il_to_nucleicacid_massratio} %`
                        : "N/A"}
                    </td>
                    <th
                      scope="row"
                      className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                    ></th>
                    <td className="w-1/4 px-6 py-4"></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="mt-8 mb-6">
              <h3 className="pl-3 mb-4 text-2xl font-semibold text-gray-600">
                Helper Lipid
              </h3>
              <div className="relative overflow-x-auto">
                <div className="mt-8 border rounded-lg shadow-md">
                  <table className="w-full text-sm">
                    <thead className="text-sm text-gray-700 uppercase bg-gray-100 rounded-t-lg">
                      <tr>
                        <th
                          scope="col"
                          className="px-6 py-3 font-medium text-gray-800 rounded-t-lg whitespace-nowrap"
                        >
                          SMILES
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="bg-white">
                        <td className="w-full px-6 py-4 text-center text-gray-700 break-all">
                          {data.hl_smiles || "N/A"}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <table className="w-full text-sm text-left">
                  <tbody>
                    <tr className="bg-white border-b">
                      <th
                        scope="row"
                        className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                      >
                        Name
                      </th>
                      <td className="w-1/4 px-6 py-4">
                        {data.hl_name || "N/A"}
                      </td>
                      <th
                        scope="row"
                        className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                      >
                        Molar Ratio
                      </th>
                      <td className="w-1/4 px-6 py-4">
                        {data.hl_molratio != null
                          ? `${data.hl_molratio} %`
                          : "N/A"}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div className="mt-8 mb-6">
              <h3 className="pl-3 mb-4 text-2xl font-semibold text-gray-600">
                PEG Lipid
              </h3>
              <div className="relative overflow-x-auto">
                <div className="mt-8 border rounded-lg shadow-md">
                  <table className="w-full text-sm">
                    <thead className="text-sm text-gray-700 uppercase bg-gray-100 rounded-t-lg">
                      <tr>
                        <th
                          scope="col"
                          className="px-6 py-3 font-medium text-gray-800 rounded-t-lg whitespace-nowrap"
                        >
                          SMILES
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="bg-white">
                        <td className="w-full px-6 py-4 text-center text-gray-700 break-all">
                          {data.peg_smiles || "N/A"}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <table className="w-full text-sm text-left">
                  <tbody>
                    <tr className="bg-white border-b">
                      <th
                        scope="row"
                        className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                      >
                        Name
                      </th>
                      <td className="w-1/4 px-6 py-4">
                        {data.peg_name || "N/A"}
                      </td>
                      <th
                        scope="row"
                        className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                      >
                        Molar Ratio
                      </th>
                      <td className="w-1/4 px-6 py-4">
                        {data.peg_molratio != null
                          ? `${data.peg_molratio} %`
                          : "N/A"}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div className="mt-8 mb-6">
              <h3 className="pl-3 mb-4 text-2xl font-semibold text-gray-600">
                Cholesterol
              </h3>
              <div className="relative overflow-x-auto">
                <div className="mt-8 border rounded-lg shadow-md">
                  <table className="w-full text-sm">
                    <thead className="text-sm text-gray-700 uppercase bg-gray-100 rounded-t-lg">
                      <tr>
                        <th
                          scope="col"
                          className="px-6 py-3 font-medium text-gray-800 rounded-t-lg whitespace-nowrap"
                        >
                          SMILES
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="bg-white">
                        <td className="w-full px-6 py-4 text-center text-gray-700 break-all">
                          {data.chl_smiles || "N/A"}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <table className="w-full text-sm text-left">
                  <tbody>
                    <tr className="bg-white border-b">
                      <th
                        scope="row"
                        className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                      >
                        Name
                      </th>
                      <td className="w-1/4 px-6 py-4">
                        {data.chl_name || "N/A"}
                      </td>
                      <th
                        scope="row"
                        className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                      >
                        Molar Ratio
                      </th>
                      <td className="w-1/4 px-6 py-4">
                        {data.chl_molratio != null
                          ? `${data.chl_molratio} %`
                          : "N/A"}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div className="mt-8 mb-6">
              <h3 className="pl-3 mb-4 text-2xl font-semibold text-gray-600">
                Buffers
              </h3>
              <div className="relative overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <tbody>
                    <tr className="bg-white border-b">
                      <th
                        scope="row"
                        className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                      >
                        Aqueous
                      </th>
                      <td className="w-1/4 px-6 py-4">{data.aqueous_buffer}</td>
                      <th
                        scope="row"
                        className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                      >
                        Dialysis
                      </th>
                      <td className="w-1/4 px-6 py-4">45</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <hr className="mb-12 border-gray-300" />
          </section>

          <section id="experimental-conditions" className="p-2">
            <h2 className="mb-6 text-4xl font-bold">Experimental Conditions</h2>

            <div className="relative overflow-x-auto">
              <table className="w-full text-sm text-left">
                <tbody>
                  <tr className="bg-white border-b">
                    <th
                      scope="row"
                      className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                    >
                      Mixing Method
                    </th>
                    <td className="w-1/4 px-6 py-4">
                      {data.mixing_method || "N/A"}
                    </td>
                    <th
                      scope="row"
                      className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                    >
                      Cargo Type
                    </th>
                    <td className="w-1/4 px-6 py-4">
                      {data.cargo_type || "N/A"}
                    </td>
                  </tr>
                  <tr className="bg-white border-b">
                    <th
                      scope="row"
                      className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                    >
                      Cargo
                    </th>
                    <td className="w-1/4 px-6 py-4">{data.cargo || "N/A"}</td>
                    <th
                      scope="row"
                      className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                    >
                      Model
                    </th>
                    <td className="w-1/4 px-6 py-4">{data.model || "N/A"}</td>
                  </tr>
                  <tr className="bg-white border-b">
                    <th
                      scope="row"
                      className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                    >
                      Model Type
                    </th>
                    <td className="w-1/4 px-6 py-4">
                      {data.model_type || "N/A"}
                    </td>
                    <th
                      scope="row"
                      className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                    >
                      Model Target
                    </th>
                    <td className="w-1/4 px-6 py-4">
                      {data.model_target || "N/A"}
                    </td>
                  </tr>
                  <tr className="bg-white border-b">
                    <th
                      scope="row"
                      className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                    >
                      Route of Administration
                    </th>
                    <td className="w-1/4 px-6 py-4">
                      {data.route_of_administration || "N/A"}
                    </td>
                    <th
                      scope="row"
                      className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                    >
                      Experiment Method
                    </th>
                    <td className="w-1/4 px-6 py-4">
                      {data.experiment_method || "N/A"}
                    </td>
                  </tr>
                  <tr className="bg-white border-b">
                    <th
                      scope="row"
                      className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                    >
                      Experiment Batching
                    </th>
                    <td className="w-1/4 px-6 py-4">
                      {data.experiment_batching || "N/A"}
                    </td>
                    <th
                      scope="row"
                      className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                    >
                      Experiment Value
                    </th>
                    <td className="w-1/4 px-6 py-4">
                      {data.experiment_value != null
                        ? data.experiment_value.toFixed(3)
                        : "N/A"}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <hr className="mb-12" />
          </section>

          <section id="molecular-properties" className="p-2">
            <h2 className="my-6 text-4xl font-bold">Molecular Properties</h2>

            <div className="mb-6">
              <h3 className="pl-3 mb-4 text-2xl font-semibold text-gray-600">
                Physical Properties
              </h3>
              <div className="relative overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <tbody>
                    <tr className="bg-white border-b">
                      <th
                        scope="row"
                        className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                      >
                        Molecular Weight
                      </th>
                      <td className="w-1/4 px-6 py-4">
                        {data.molecular_weight
                          ? data.molecular_weight.toFixed(2)
                          : "N/A"}
                      </td>
                      <th
                        scope="row"
                        className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                      >
                        Heavy Atoms
                      </th>
                      <td className="w-1/4 px-6 py-4">
                        {data.heavy_atoms || "N/A"}
                      </td>
                    </tr>
                    <tr className="bg-white border-b"></tr>
                    <tr className="bg-white border-b">
                      <th
                        scope="row"
                        className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                      >
                        Rings
                      </th>
                      <td className="w-1/4 px-6 py-4">{data.rings || "N/A"}</td>
                      <th
                        scope="row"
                        className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                      >
                        Aromatic Rings
                      </th>
                      <td className="w-1/4 px-6 py-4">
                        {data.aromatic_rings || "N/A"}
                      </td>
                    </tr>
                    <tr className="bg-white border-b">
                      <th
                        scope="row"
                        className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                      >
                        Rotatable Bonds
                      </th>
                      <td className="w-1/4 px-6 py-4">
                        {data.rotatable_bonds || "N/A"}
                      </td>
                      <th
                        scope="row"
                        className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                      >
                        Van der Waals Molecular Volume
                      </th>
                      <td className="w-1/4 px-6 py-4">
                        {data.van_der_waals_molecular_volume
                          ? data.van_der_waals_molecular_volume.toFixed(2)
                          : "N/A"}
                      </td>
                    </tr>
                    <tr className="bg-white border-b">
                      <th
                        scope="row"
                        className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                      >
                        SP3 Carbons
                      </th>
                      <td className="w-1/4 px-6 py-4">
                        {data.sp3_carbons || "N/A"}
                      </td>
                      <th
                        scope="row"
                        className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                      >
                        Fraction SP3 Carbons
                      </th>
                      <td className="w-1/4 px-6 py-4">
                        {data.fraction_sp3_carbons
                          ? data.fraction_sp3_carbons.toFixed(3)
                          : "N/A"}
                      </td>
                    </tr>
                    <tr className="bg-white border-b">
                      <th
                        scope="row"
                        className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                      >
                        Nitrogen Count
                      </th>
                      <td className="w-1/4 px-6 py-4">
                        {data.nitrogen_count || "N/A"}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-8 mb-6">
              <h3 className="pl-3 mb-4 text-2xl font-semibold text-gray-600">
                Chemical Properties
              </h3>
              <div className="relative overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <tbody>
                    <tr className="bg-white border-b">
                      <th
                        scope="row"
                        className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                      >
                        Topological Polar Surface Area
                      </th>
                      <td className="w-1/4 px-6 py-4">
                        {data.topological_polar_surface_area
                          ? data.topological_polar_surface_area.toFixed(2)
                          : "N/A"}
                      </td>
                      <th
                        scope="row"
                        className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                      >
                        Hydrogen Bond Donors
                      </th>
                      <td className="w-1/4 px-6 py-4">
                        {data.hydrogen_bond_donors || "N/A"}
                      </td>
                    </tr>
                    <tr className="bg-white border-b">
                      <th
                        scope="row"
                        className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                      >
                        Hydrogen Bond Acceptors
                      </th>
                      <td className="w-1/4 px-6 py-4">
                        {data.hydrogen_bond_acceptors || "N/A"}
                      </td>
                      <th
                        scope="row"
                        className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                      >
                        LogP
                      </th>
                      <td className="w-1/4 px-6 py-4">
                        {data.logp ? data.logp.toFixed(3) : "N/A"}
                      </td>
                    </tr>
                    <tr className="bg-white border-b">
                      <th
                        scope="row"
                        className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                      >
                        Molar Refractivity
                      </th>
                      <td className="w-1/4 px-6 py-4">
                        {data.molar_refractivity
                          ? data.molar_refractivity.toFixed(3)
                          : "N/A"}
                      </td>
                      <th
                        scope="row"
                        className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                      ></th>
                      <td className="w-1/4 px-6 py-4"></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>
          <section id="publication" className="p-2">
            <h2 className="my-6 text-4xl font-bold">Publication</h2>
            <div className="mt-8 mb-6">
              <h3 className="pl-3 mb-4 text-2xl font-semibold text-gray-600">
                Publication Information
              </h3>
              <div className="relative overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <tbody>
                    <tr className="bg-white border-b">
                      <th
                        scope="row"
                        className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                      >
                        Publication Link
                      </th>
                      <td className="w-3/4 px-6 py-4">
                        {data.publication_doi ? (
                          <a
                            href={data.publication_doi}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            {data.publication_doi}
                          </a>
                        ) : (
                          "N/A"
                        )}
                      </td>
                    </tr>
                    <tr className="bg-white border-b">
                      <th
                        scope="row"
                        className="w-1/4 px-6 py-4 font-medium text-gray-900 bg-gray-100 whitespace-nowrap"
                      >
                        Publication PMID
                      </th>
                      <td className="w-3/4 px-6 py-4">
                        {data.publication_pmid ? (
                          <a
                            href={`https://pubmed.ncbi.nlm.nih.gov/${data.publication_pmid}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            {data.publication_pmid}
                          </a>
                        ) : (
                          "N/A"
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default DetailPage;
